import React from "react";
import { Dropdown } from "react-bootstrap";

// "Añadir a mi calendario" (E9 — parte A). Enlace a Google Calendar + descarga
// de un .ics (Apple/Outlook). Sin backend: se arma en el cliente.
//
// Las clases se guardan como hora de pared en America/Bogota (UTC-5, sin horario
// de verano). Para Google usamos ctz=America/Bogota + hora local; para el .ics
// convertimos a UTC sumando 5h.
const BOGOTA_OFFSET_H = 5;
const pad = (n) => String(n).padStart(2, "0");

function partsOf(dateStr, timeStr) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const [hh, mm] = String(timeStr || "00:00").split(":").map(Number);
  return { y, m, d, hh, mm };
}

// Aritmética de hora de pared (tz-agnóstica) vía Date.UTC.
function fmtLocalFromMs(ms) {
  const dt = new Date(ms);
  return (
    `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}` +
    `T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00`
  );
}
function fmtUtcZ(dt) {
  return (
    `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}` +
    `T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}${pad(dt.getUTCSeconds())}Z`
  );
}

function buildTimes({ date, timeStart, duration }) {
  const { y, m, d, hh, mm } = partsOf(date, timeStart);
  const durH = Number(duration) || 1;
  const startWallMs = Date.UTC(y, m - 1, d, hh, mm);
  const endWallMs = startWallMs + durH * 3600000;
  const startUtc = new Date(Date.UTC(y, m - 1, d, hh + BOGOTA_OFFSET_H, mm, 0));
  const endUtc = new Date(startUtc.getTime() + durH * 3600000);
  return {
    googleStart: fmtLocalFromMs(startWallMs),
    googleEnd: fmtLocalFromMs(endWallMs),
    icsStart: fmtUtcZ(startUtc),
    icsEnd: fmtUtcZ(endUtc),
  };
}

function googleUrl(ev) {
  const t = buildTimes(ev);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title || "Clase",
    dates: `${t.googleStart}/${t.googleEnd}`,
    ctz: "America/Bogota",
  });
  if (ev.description) params.set("details", ev.description);
  if (ev.location) params.set("location", ev.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function icsContent(ev, uid) {
  const t = buildTimes(ev);
  const esc = (s) =>
    String(s || "")
      .replace(/([,;\\])/g, "\\$1")
      .replace(/\n/g, "\\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Diezlar//Clases//ES",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${fmtUtcZ(new Date())}`,
    `DTSTART:${t.icsStart}`,
    `DTEND:${t.icsEnd}`,
    `SUMMARY:${esc(ev.title || "Clase")}`,
    ev.location ? `LOCATION:${esc(ev.location)}` : null,
    ev.description ? `DESCRIPTION:${esc(ev.description)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

function downloadIcs(ev, uid) {
  const blob = new Blob([icsContent(ev, uid)], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${uid}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Botón desplegable "Añadir a mi calendario".
 * Props: title, date ("YYYY-MM-DD"), timeStart ("HH:mm"), duration (horas),
 * location, description, uid, y opcionalmente size/variant.
 */
function AddToCalendar({
  title,
  date,
  timeStart,
  duration,
  location,
  description,
  uid,
  size,
  variant = "outline-secondary",
}) {
  if (!date) return null;
  const ev = { title, date, timeStart, duration, location, description };
  const id = String(uid ?? `${date}-${timeStart || "0000"}`);
  return (
    <Dropdown>
      <Dropdown.Toggle variant={variant} size={size} id={`addcal-${id}`}>
        <i className="mdi mdi-calendar-plus me-1" aria-hidden="true" />
        Añadir a mi calendario
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item
          href={googleUrl(ev)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="mdi mdi-google me-2" aria-hidden="true" />
          Google Calendar
        </Dropdown.Item>
        <Dropdown.Item as="button" onClick={() => downloadIcs(ev, `diezlar-${id}`)}>
          <i className="mdi mdi-calendar-export me-2" aria-hidden="true" />
          Descargar .ics (Apple/Outlook)
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export { AddToCalendar };
